import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";

const pathSection = "/quiz/api/v1";
export const SectionModel = types
  .model({
    id: types.number,
    title: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

const QuizSectionStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathSection}/section`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: SectionModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(SectionModel),
    isMoreLoading: types.optional(types.boolean, false),
    isSectionLoading: types.optional(types.boolean, false),
    isSectionManaging: types.optional(types.boolean, false),
    isSectionPublishing: types.optional(types.boolean, false),
    isSectionCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get quizOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, code, publishStatus }) => ({
        label: title,
        value: {
          id,
          title,
          code,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    updateSection(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreSections: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(
            `${pathSection}/section`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              search: self.search,
              orderBy,
              orderByDesc,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchSectionDetails: flow(function*(id?: number) {
      self.isSectionLoading = true;

      try {
        const details = yield api.get(
          `${pathSection}/section/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isSectionLoading = false;
      }
    }),

    createSection: flow(function*(payload: any) {
      self.isSectionManaging = true;

      try {
        const { id } = yield api.post(
          `${pathSection}/section`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isSectionManaging = false;
      }
    }),

    editSectionInfo: flow(function*(payload: any) {
      self.isSectionManaging = true;

      try {
        return yield api.put(
          `${pathSection}/section/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isSectionManaging = false;
      }
    }),

    changeSectionStatus: flow(function*(id: number, status: string) {
      self.isSectionPublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${pathSection}/section/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateSection(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isSectionPublishing = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default QuizSectionStore.create();
